/**
 * SCM19 — Photo + delivery confirmation.
 *
 * When the site team uploads a "transformer arrived" photo, this module:
 *
 *   1. Records the photo metadata against the BoM item as a
 *      `DeliveryReceipt` — fileName, document id, photographer uid,
 *      latitude/longitude (optional), and the receipt timestamp.
 *   2. Auto-transitions the BoM item's status to `on-site` via
 *      `applyStatusTransition` (forward step — monotonic).
 *   3. Writes a SCM15 status-change-log event tagged with the receipt
 *      doc id so the audit trail joins the photo back to the state
 *      transition.
 *   4. Returns a new SupplyChainPlan with the merged BoM, plus the
 *      derived alerts (SCM16) and critical-path snapshot (SCM5)
 *      reflecting the new state. The caller persists the plan + the
 *      audit row; this module stays IO-agnostic.
 *
 * Reuses the existing document-upload pipeline — callers pass a
 * `SiteDocument` produced by `processUploadedFile` so no parallel
 * persistence path is introduced.
 *
 * Pure / synchronous wrt the BoM merge. The audit-log write is async
 * (delegated to the SCM15 writer); the merger function returns BOTH
 * the new plan AND the unsaved event so the caller can run them in
 * the order it prefers (write plan first, then audit; or the reverse
 * with rollback).
 */
import type { SiteDocument } from '../../types/siteRag';
import type {
  BomItem,
  BomItemStatus,
  DeliveryReceipt,
  SupplyChainPlan,
} from '../../types/supplyChain';
import {
  applyStatusTransition,
  buildStatusChangeEvent,
  type BomStatusChangeEvent,
} from '../../lib/bomItemStatusFlow';
import {
  computeCriticalPath,
  type CriticalPathResult,
} from '../../lib/criticalPathAnalysis';
import {
  deriveProcurementAlerts,
  type ProcurementAlert,
} from '../../lib/procurementAlerts';

/** Type-guard for callers that need to narrow `BomItem`. */
export function hasDeliveryReceipt(
  item: BomItem,
): item is BomItem & { deliveryReceipt: DeliveryReceipt } {
  return !!item.deliveryReceipt;
}

// ─────────────────────────────────────────────────────────────────────────────
// Input args
// ─────────────────────────────────────────────────────────────────────────────

export interface RecordDeliveryReceiptArgs {
  plan: SupplyChainPlan;
  bomItemId: string;
  /** The SiteDocument produced by the existing upload pipeline. */
  document: Pick<SiteDocument, 'id' | 'fileName' | 'mimeType' | 'uploadedAt'>;
  /** Optional location capture. */
  lat?: number;
  lng?: number;
  /** Optional site-team note ("delivered to bay 3 — visible damage on
   *  crate, opened for inspection"). */
  notes?: string;
  /** UID of the uploader — required for audit attribution. */
  actorUid: string;
  /** Display name / email of the uploader. */
  actorEmail: string;
  /** Site id — required for the status-change-log row. */
  siteId: string;
  /** COD — drives the SCM16 alert recompute downstream. Pass null when
   *  the project hasn't fixed a COD yet. */
  inServiceDate: string | null;
  /** Override `now` for deterministic tests. */
  now?: string;
  /** Target status — defaults to 'on-site' but a future "delivery scan"
   *  workflow might land at 'in-transit' first; the function honours
   *  whatever forward state the caller wants. */
  targetStatus?: BomItemStatus;
}

export interface RecordDeliveryReceiptResult {
  /** New plan with the merged BoM. Callers persist this. */
  plan: SupplyChainPlan;
  /** The updated BoM item — exposed for callers that want to render
   *  it in a confirmation toast without re-scanning the plan. */
  item: BomItem;
  /** The status-change-log row to persist via
   *  `services/supplyChain/bomStatusChangeLog.ts`. `null` when the
   *  item was already at the target state (no-op). */
  statusChangeEvent: BomStatusChangeEvent | null;
  /** Refreshed alert list — caller can re-dispatch / re-render. */
  alerts: ProcurementAlert[];
  /** Refreshed critical-path snapshot. */
  criticalPath: CriticalPathResult;
}

// ─────────────────────────────────────────────────────────────────────────────
// Implementation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pure: merges the receipt into the plan + computes derived views.
 * No IO. Callers persist the returned plan + statusChangeEvent.
 *
 * Throws when:
 *   - The BoM item id isn't in the plan.
 *   - The target status isn't reachable forward from the item's
 *     current state (use `revert: true` via SCM15 helpers explicitly
 *     for backward steps — this module doesn't permit them).
 */
export function recordDeliveryReceipt(
  args: RecordDeliveryReceiptArgs,
): RecordDeliveryReceiptResult {
  const targetStatus: BomItemStatus = args.targetStatus ?? 'on-site';
  const idx = args.plan.bom.findIndex((i) => i.id === args.bomItemId);
  if (idx === -1) {
    throw new Error(`BoM item ${args.bomItemId} not found in plan`);
  }
  const current = args.plan.bom[idx];

  const receipt: DeliveryReceipt = {
    schemaVersion: 1,
    documentId: args.document.id,
    fileName: args.document.fileName,
    mimeType: args.document.mimeType,
    receivedAt: args.document.uploadedAt ?? args.now ?? new Date().toISOString(),
    receivedByUid: args.actorUid,
    receivedByLabel: args.actorEmail,
    lat: args.lat,
    lng: args.lng,
    notes: args.notes,
  };

  // Status transition. When the item is already at the target state
  // the helper returns the same reference — we still attach the
  // receipt so the photo gets recorded.
  const transitioned = applyStatusTransition(current, targetStatus);
  const merged: BomItem = {
    ...(transitioned as BomItem),
    deliveryReceipt: receipt,
  };

  const newBom = args.plan.bom.slice();
  newBom[idx] = merged;
  const nowIso = args.now ?? new Date().toISOString();
  const newPlan: SupplyChainPlan = {
    ...args.plan,
    bom: newBom,
    updatedAt: nowIso,
    updatedBy: args.actorEmail,
  };

  let statusChangeEvent: BomStatusChangeEvent | null = null;
  if (current.status !== targetStatus) {
    const reasonNote = args.notes
      ? `Delivery photo: ${args.document.fileName} — ${args.notes}`
      : `Delivery photo: ${args.document.fileName}`;
    const { event } = buildStatusChangeEvent({
      siteId: args.siteId,
      item: current,
      toStatus: targetStatus,
      actorUid: args.actorUid,
      actorEmail: args.actorEmail,
      reason: reasonNote,
      now: nowIso,
    });
    statusChangeEvent = event;
  }

  const criticalPath = computeCriticalPath(newPlan.bom, args.inServiceDate, {
    now: args.now,
  });
  const alerts = deriveProcurementAlerts({
    plan: newPlan,
    inServiceDate: args.inServiceDate,
    siteId: args.siteId,
    options: args.now ? { now: args.now } : undefined,
  });

  return {
    plan: newPlan,
    item: merged,
    statusChangeEvent,
    alerts,
    criticalPath,
  };
}

/**
 * Sentinel string tag — applied to the `tags` field of the
 * `SiteDocument` so the existing document panel can filter
 * "delivery photos" without a new index. The on-upload caller can
 * append this tag when it detects an image MIME + an active BoM item
 * association.
 */
export const DELIVERY_RECEIPT_DOC_TAG = 'scm:delivery-receipt';

/**
 * Heuristic: does this document look like a delivery photo? Used by
 * the upload-completion hook to surface a "Attach to BoM item" CTA.
 *
 *   - MIME type starts with `image/`
 *   - OR filename hints at a packing slip / shipping doc
 */
export function looksLikeDeliveryReceipt(
  doc: Pick<SiteDocument, 'fileName' | 'mimeType' | 'tags'>,
): boolean {
  const mime = (doc.mimeType ?? '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  if (doc.tags?.includes(DELIVERY_RECEIPT_DOC_TAG)) return true;
  const fname = (doc.fileName ?? '').toLowerCase();
  return /packing.?slip|delivery|bill.?of.?lading|arrival/.test(fname);
}
